import { useState } from 'react';
import { useEnquiryModal } from '@/hooks/useEnquiryModal';
import FadeInSection from '@/components/base/FadeInSection';

export default function ContactPreview() {
  const { openEnquiry } = useEnquiryModal();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    products: '',
    quantity: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.name || !formData.phone || !formData.email) {
      setError('Please fill in all required fields.');
      return;
    }

    setSubmitting(true);

    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('company', formData.company);
      body.append('phone', formData.phone);
      body.append('email', formData.email);
      body.append('products', formData.products);
      body.append('quantity', formData.quantity);
      body.append('message', formData.message);

      await fetch('https://readdy.ai/api/form/d8mh2vr97o3u3g1u5ns0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      setSubmitted(true);
      setFormData({ name: '', company: '', phone: '', email: '', products: '', quantity: '', message: '' });
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="w-full py-14 md:py-20 bg-background-50">
      <FadeInSection>
        <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
          {/* Contact Info */}
          <div>
            <span className="inline-block px-4 py-1.5 bg-accent-100 text-accent-800 text-xs font-semibold rounded-full mb-4">
              Get In Touch
            </span>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4">
              Contact <strong className="text-primary-600">Bassi Enterprises</strong>
            </h2>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-8">
              Reach out for bulk supply inquiries, partnership discussions, or any questions about our fresh produce supply services.
            </p>

            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-2-line text-primary-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-heading font-semibold text-foreground-900 text-sm mb-1">Address</h4>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    248, Village Haiderpur, Shamli-Panipat Marg, Kairana,<br />
                    Shamli, Uttar Pradesh, India<br />
                    <strong>PIN 247774</strong>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-primary-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-heading font-semibold text-foreground-900 text-sm mb-1">Phone</h4>
                  <a
                    href="tel:+919084756574"
                    className="text-foreground-600 hover:text-primary-600 text-sm transition-colors duration-200"
                  >
                    +91 9084756574
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-primary-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-heading font-semibold text-foreground-900 text-sm mb-1">Email</h4>
                  <a
                    href="mailto:basharatrahi8043@gmail.com"
                    className="text-foreground-600 hover:text-primary-600 text-sm transition-colors duration-200"
                  >
                    basharatrahi8043@gmail.com
                  </a>
                </div>
              </div>
            </div>

            {/* Quick Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <a
                href="tel:+919084756574"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-fill text-lg"></i>
                Call Now
              </a>
              <button
                onClick={() => openEnquiry()}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#25D366] text-background-50 text-sm font-semibold rounded-full hover:bg-[#20BD5A] transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-fill text-lg"></i>
                WhatsApp
              </button>
            </div>
          </div>

          {/* Enquiry Form */}
          <div>
            <div className="bg-background-100 border border-background-200 rounded-2xl p-6 md:p-8">
              <h3 className="font-heading font-semibold text-lg text-foreground-900 mb-6">
                Send Supplier Inquiry
              </h3>

              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-primary-600 text-2xl"></i>
                  </div>
                  <h4 className="font-heading font-semibold text-lg text-foreground-900 mb-2">Inquiry Sent!</h4>
                  <p className="text-foreground-600 text-sm">Thank you for reaching out. We&apos;ll get back to you shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="true" className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        Name <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        Company Name
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="Your company"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="phone" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        Phone Number <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="+91 98765 43210"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        Email <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="products" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        Products Required
                      </label>
                      <input
                        type="text"
                        id="products"
                        name="products"
                        value={formData.products}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="e.g. Tomatoes, Onions"
                      />
                    </div>
                    <div>
                      <label htmlFor="quantity" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        Expected Quantity
                      </label>
                      <input
                        type="text"
                        id="quantity"
                        name="quantity"
                        value={formData.quantity}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="e.g. 100 kg weekly"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="Tell us about your requirements..."
                    ></textarea>
                    <p className="text-foreground-400 text-xs mt-1">{formData.message.length}/500</p>
                  </div>

                  {error && (
                    <p className="text-accent-600 text-sm font-medium">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Sending...' : 'Send Supplier Inquiry'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
        </div>
      </FadeInSection>
    </section>
  );
}